/**
 * ConfirmDialog — a themed replacement for window.confirm()/window.alert().
 *
 * Native browser dialogs can't be styled, block the whole tab, and look
 * completely off-brand. This is a single global modal (mounted once, in
 * App.js) plus two imperative helpers — `confirmDialog()` and
 * `alertDialog()` — that any file can import and `await`, same calling
 * convention as `window.confirm`/`window.alert` but themed and promise-based.
 *
 * Usage:
 *   import { confirmDialog } from '@/components/ConfirmDialog';
 *   const ok = await confirmDialog('Delete this section? This cannot be undone.');
 *   if (!ok) return;
 *
 *   const ok = await confirmDialog({ title: 'Delete chapter', message: '...', danger: true, confirmLabel: 'Delete' });
 */
import React, { useEffect, useState } from 'react';
import { AlertTriangle, Info } from 'lucide-react';

let notify = null; // set by the mounted <ConfirmDialogHost/>

function open(options) {
  return new Promise((resolve) => {
    if (!notify) { resolve(true); return; } // host not mounted yet — fail open rather than hang
    notify({ ...options, resolve });
  });
}

/** Themed confirm — resolves `true` on confirm, `false` on cancel/dismiss. */
export function confirmDialog(options) {
  const opts = typeof options === 'string' ? { message: options } : (options || {});
  return open({ mode: 'confirm', confirmLabel: 'Confirm', cancelLabel: 'Cancel', ...opts });
}

/** Themed alert — resolves (no value) once dismissed. Single "OK" button. */
export function alertDialog(options) {
  const opts = typeof options === 'string' ? { message: options } : (options || {});
  return open({ mode: 'alert', confirmLabel: 'OK', ...opts });
}

/** Mount once near the app root (alongside <Toaster/>). */
export function ConfirmDialogHost() {
  const [req, setReq] = useState(null);

  useEffect(() => {
    notify = (r) => setReq(r);
    return () => { notify = null; };
  }, []);

  useEffect(() => {
    if (!req) return;
    const onKey = (e) => {
      if (e.key === 'Escape') finish(false);
      if (e.key === 'Enter') finish(true);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [req]);

  if (!req) return null;

  const finish = (value) => {
    req.resolve(value);
    setReq(null);
  };

  const danger = !!req.danger;
  const Icon = danger ? AlertTriangle : Info;
  const accent = danger ? '#DC2626' : '#34A853';
  const accentSoft = danger ? '#FEF2F2' : '#F0FDF4';

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/40 backdrop-blur-[1px]"
      onMouseDown={(e) => { if (e.target === e.currentTarget) finish(false); }}
      data-testid="confirm-dialog-overlay"
    >
      <div
        className="w-full max-w-sm rounded-2xl bg-white overflow-hidden"
        style={{ boxShadow: '0 24px 60px -12px rgba(15,23,42,0.28), 0 8px 24px -8px rgba(15,23,42,0.14)' }}
        data-testid="confirm-dialog"
      >
        <div className="p-5 flex items-start gap-3">
          <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: accentSoft }}>
            <Icon className="w-4.5 h-4.5" style={{ color: accent }} />
          </div>
          <div className="flex-1 min-w-0 pt-0.5">
            {req.title && (
              <h3 className="text-[15px] font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{req.title}</h3>
            )}
            <p className="text-[13px] text-slate-600 mt-1 leading-relaxed">{req.message}</p>
          </div>
        </div>
        <div className="px-5 pb-5 flex items-center justify-end gap-2">
          {req.mode === 'confirm' && (
            <button
              onClick={() => finish(false)}
              className="h-9 px-4 rounded-lg text-[13px] font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
              data-testid="confirm-dialog-cancel"
              autoFocus
            >
              {req.cancelLabel || 'Cancel'}
            </button>
          )}
          <button
            onClick={() => finish(true)}
            className="h-9 px-4 rounded-lg text-[13px] font-bold text-white transition-all active:scale-[0.97] hover:shadow-md"
            style={{ background: danger ? '#DC2626' : 'linear-gradient(135deg, #34A853 0%, #0d9488 100%)' }}
            data-testid="confirm-dialog-confirm"
          >
            {req.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
